/**
 * How a Vault file is named to the person (owner, 20 Oct 2026 — D4: the Vault
 * is aware of where its files are used, and says it in their words):
 *
 *   • its topic — what the document is (D2) when a hero said it, else what
 *     its hero filed it under (a pitch video, a TelePortal track…), never the
 *     stored id (`pitch_video`, `legal`);
 *   • each record that links it — an InOut trip (its dates, the item it
 *     proves), a TelePortal task's document (what the task asked for).
 *
 * Literal keys only, so the key-usage gate reads every one; the dates are the
 * caller's to format in the person's locale.
 */
import type { VaultFileLink } from '@tonnie/types';

import {
  documentTypeLabelKey,
  type DOCUMENT_TYPE_LABEL_KEY,
  type DocumentType,
} from './document-types';

type DocumentTypeLabelKey = (typeof DOCUMENT_TYPE_LABEL_KEY)[DocumentType];

/**
 * What a hero files a document under when it is not a document type
 * (`vault_files.subcategory`): CheckUp's pitch video and language test, a CV
 * a company imported, a TelePortal track a company's document belongs to.
 */
export const VAULT_TOPICS = [
  'pitch_video',
  'language_test',
  'cv',
  'legal',
  'travel',
  'setup',
] as const;
export type VaultTopic = (typeof VAULT_TOPICS)[number];

export const VAULT_TOPIC_LABEL_KEY = {
  pitch_video: 'heroes.vault.topic.pitchVideo',
  language_test: 'heroes.vault.topic.languageTest',
  cv: 'heroes.vault.topic.cv',
  legal: 'heroes.vault.track.legal',
  travel: 'heroes.vault.track.travel',
  setup: 'heroes.vault.track.setup',
} as const satisfies Record<VaultTopic, string>;

export type VaultTopicLabelKey = DocumentTypeLabelKey | (typeof VAULT_TOPIC_LABEL_KEY)[VaultTopic];

function isVaultTopic(value: string): value is VaultTopic {
  return (VAULT_TOPICS as readonly string[]).includes(value);
}

/** Every way a link is said — one key each, see {@link vaultFileLinkLabel}. */
export const VAULT_FILE_LINK_LABEL_KEYS = [
  'heroes.vault.links.trip',
  'heroes.vault.links.tripAway',
  'heroes.vault.links.task',
  'heroes.vault.links.taskUnnamed',
] as const;

/**
 * The label key of a file's topic — its type when known, else what its hero
 * filed it under. Null when neither is one the product names (nothing shown,
 * never a raw id).
 */
export function vaultFileTopicLabelKey(file: {
  documentType?: string | null;
  subTopic?: string | null;
}): VaultTopicLabelKey | null {
  const typed = documentTypeLabelKey(file.documentType) ?? documentTypeLabelKey(file.subTopic);
  if (typed) return typed;
  return file.subTopic && isVaultTopic(file.subTopic) ? VAULT_TOPIC_LABEL_KEY[file.subTopic] : null;
}

/** One record that links a file, as the person reads it. */
export type VaultFileLinkLabel =
  | {
      kind: 'inoutTrip';
      /** `{{from}}`–`{{to}}`, or from `{{from}}` while the person is still away. */
      key: 'heroes.vault.links.trip' | 'heroes.vault.links.tripAway';
      from: string;
      to: string | null;
      /** The evidence item the file proves on that trip — null when the country's item is not one we name. */
      itemKey: DocumentTypeLabelKey | null;
    }
  | {
      kind: 'teleportalDocument';
      /** `{{document}}` when the task's document is one we name. */
      key: 'heroes.vault.links.task' | 'heroes.vault.links.taskUnnamed';
      documentKey: DocumentTypeLabelKey | null;
    };

export function vaultFileLinkLabel(link: VaultFileLink): VaultFileLinkLabel {
  switch (link.kind) {
    case 'inoutTrip':
      return {
        kind: 'inoutTrip',
        key: link.returnedOn ? 'heroes.vault.links.trip' : 'heroes.vault.links.tripAway',
        from: link.departedOn,
        to: link.returnedOn,
        itemKey: documentTypeLabelKey(link.itemId),
      };
    case 'teleportalDocument': {
      const documentKey = documentTypeLabelKey(link.documentType);
      return {
        kind: 'teleportalDocument',
        key: documentKey ? 'heroes.vault.links.task' : 'heroes.vault.links.taskUnnamed',
        documentKey,
      };
    }
    default: {
      const unknown: never = link;
      return unknown;
    }
  }
}
